import React from 'react'
import { Link, useLocation } from 'wouter'
import {
  Activity,
  Archive,
  Award,
  Bell,
  Bot,
  Brain,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Database,
  DollarSign,
  HeadphonesIcon,
  Key,
  Lightbulb,
  LogOut,
  MapPin,
  Monitor,
  Package,
  Percent,
  PieChart,
  Radar,
  Settings,
  ShoppingCart,
  Sparkles,
  Store,
  Target,
  TrendingUp,
  Upload,
  User,
  Users,
  Zap,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'

interface AppSidebarProps {
  isOpen: boolean
  onToggle: () => void
}

type SidebarIcon = React.ComponentType<{ className?: string }>

interface NavItem {
  name: string
  href: string
  icon: SidebarIcon
  activeMatch?: (location: string) => boolean
}

interface NavSection {
  title: string
  items: NavItem[]
  icon: SidebarIcon
  collapsible?: boolean
  defaultExpanded?: boolean
}

const isExactOrChild = (location: string, href: string) =>
  location === href || location.startsWith(`${href}/`)

const hasActiveMatch = (item: NavItem, location: string) =>
  item.activeMatch ? item.activeMatch(location) : isExactOrChild(location, item.href)

const navSections: NavSection[] = [
  {
    title: 'Intelligence',
    icon: Lightbulb,
    items: [
      {
        name: 'Intelligence Hub',
        href: '/intelligence',
        icon: Lightbulb,
        activeMatch: (location) => isExactOrChild(location, '/intelligence'),
      },
      { name: 'Daily Alerts', href: '/alerts', icon: Bell },
      { name: 'Competitive Radar', href: '/competitive-radar', icon: Radar },
    ],
    defaultExpanded: true,
  },
  {
    title: 'Retail Performance',
    icon: Briefcase,
    items: [
      { name: 'Overview', href: '/retail', icon: Briefcase },
      { name: 'Retail Analytics', href: '/retail-performance', icon: TrendingUp },
      { name: 'Social Halo', href: '/social-halo', icon: Activity },
      { name: 'Products', href: '/products', icon: Package },
      { name: 'Stores', href: '/stores', icon: Store },
      { name: 'Inventory', href: '/inventory', icon: Archive },
      { name: 'Pricing & Margin', href: '/pricing-margin', icon: Percent },
      { name: 'Distribution', href: '/distribution-availability', icon: Target },
      { name: 'Risk Analysis', href: '/concentration-risk', icon: PieChart },
    ],
    defaultExpanded: true,
  },
  {
    title: 'DTC',
    icon: ShoppingCart,
    items: [
      { name: 'DTC Summary', href: '/dtc/overview', icon: ShoppingCart },
      { name: 'Amazon', href: '/dtc/amazon', icon: ShoppingCart },
      { name: 'Shopify', href: '/dtc/shopify', icon: Store },
      { name: 'Walmart', href: '/dtc/walmart', icon: ShoppingCart },
    ],
    defaultExpanded: true,
  },
  {
    title: 'AI Copilot',
    icon: Bot,
    items: [
      { name: 'Ask PIQ', href: '/agent-hub', icon: Bot },
      { name: 'Command Center', href: '/command-center', icon: Zap },
      { name: 'Store Intelligence', href: '/ai/store-cluster-intelligence', icon: MapPin },
      { name: 'Inventory Intel', href: '/ai/inventory-intelligence', icon: Package },
      { name: 'Promo Optimizer', href: '/ai/promo-optimizer', icon: Sparkles },
    ],
    defaultExpanded: true,
  },
  {
    title: 'Advocates',
    icon: Award,
    items: [
      { name: 'Overview', href: '/advocate-opus', icon: Award },
      {
        name: 'Discovery',
        href: '/advocate-opus/discovery',
        icon: Users,
        activeMatch: (location) =>
          location === '/advocate-opus/discovery' || location === '/advocate-opus/discovery-old',
      },
      { name: 'Offers', href: '/advocate-opus/offers', icon: DollarSign },
      { name: 'Payouts', href: '/advocate-opus/payouts', icon: CreditCard },
    ],
    defaultExpanded: true,
  },
  {
    title: 'Admin',
    icon: Settings,
    items: [
      { name: 'Retail Support', href: '/retail-support', icon: HeadphonesIcon },
      { name: 'Data Monitor', href: '/admin/data-monitor', icon: Monitor },
      { name: 'Ingestion Config', href: '/admin/ingestion-config', icon: Upload },
      { name: 'API Keys', href: '/admin/api-keys', icon: Key },
      { name: 'Database Audit', href: '/admin/database-audit', icon: Database },
      { name: 'Knowledge Base', href: '/admin/knowledge-base', icon: Brain },
    ],
    collapsible: true,
    defaultExpanded: false,
  },
]

function SidebarSection({
  section,
  isOpen,
  location,
}: {
  section: NavSection
  isOpen: boolean
  location: string
}) {
  const hasActiveItem = section.items.some((item) => hasActiveMatch(item, location))
  const [isExpanded, setIsExpanded] = React.useState(section.defaultExpanded ?? true)

  React.useEffect(() => {
    if (hasActiveItem) {
      setIsExpanded(true)
    }
  }, [hasActiveItem])

  const showItems = section.collapsible ? isExpanded : true

  if (!isOpen) {
    return (
      <>
        {section.items.map((item) => {
          const Icon = item.icon
          const isActive = hasActiveMatch(item, location)
          return (
            <Link key={item.name} href={item.href}>
              <div
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
                title={item.name}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
              </div>
            </Link>
          )
        })}
      </>
    )
  }

  const SectionIcon = section.icon

  return (
    <div className="mb-2">
      <button
        onClick={() => {
          if (section.collapsible) {
            setIsExpanded((prev) => !prev)
          }
        }}
        className={`w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors uppercase tracking-wider ${
          section.collapsible ? '' : 'cursor-default'
        }`}
      >
        <div className="flex items-center gap-2">
          <SectionIcon className="w-4 h-4" />
          <span>{section.title}</span>
        </div>
        {section.collapsible && (
          <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
        )}
      </button>

      {showItems && (
        <div className="space-y-1 mt-1">
          {section.items.map((item) => {
            const Icon = item.icon
            const isActive = hasActiveMatch(item, location)
            return (
              <Link key={item.name} href={item.href}>
                <div
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-medium">{item.name}</span>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}

export function AppSidebar({ isOpen, onToggle }: AppSidebarProps) {
  const [location] = useLocation()
  const { user, signOut } = useAuth()

  return (
    <div
      className={`fixed left-0 top-0 z-50 h-full bg-card border-r border-border transition-all duration-300 flex flex-col ${
        isOpen ? 'w-64' : 'w-16'
      }`}
    >
      <div className="flex h-16 items-center justify-between px-4 border-b border-border flex-shrink-0">
        {isOpen && (
          <div className="flex items-center space-x-2">
            <img src="/logo.svg" alt="PatternIQ" className="w-32 h-8 text-primary" />
          </div>
        )}
        <button onClick={onToggle} className="p-2 rounded-lg hover:bg-accent transition-colors">
          {isOpen ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
      </div>

      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto overflow-x-hidden">
        {navSections.map((section) => (
          <SidebarSection key={section.title} section={section} isOpen={isOpen} location={location} />
        ))}
      </nav>

      <div className="px-4 py-4 border-t border-border flex-shrink-0">
        {isOpen && user && (
          <div className="mb-4">
            <div className="flex items-center space-x-3 px-3 py-2 rounded-lg bg-accent/50">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <User className="w-4 h-4 text-primary-foreground" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user.given_name || user.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
              </div>
            </div>
            <button
              onClick={signOut}
              className="w-full mt-2 flex items-center space-x-3 px-3 py-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-destructive/10 transition-colors"
            >
              <LogOut className="w-4 h-4 flex-shrink-0" />
              <span className="text-sm font-medium">Sign Out</span>
            </button>
          </div>
        )}
        {!isOpen && user && (
          <button
            onClick={signOut}
            className="w-full flex items-center justify-center p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-destructive/10 transition-colors"
            title="Sign Out"
          >
            <LogOut className="w-5 h-5" />
          </button>
        )}
        {isOpen && (
          <div className="text-xs text-muted-foreground">
            <p>PatternIQ v1.0.0</p>
            <p>Retail Analytics Platform</p>
          </div>
        )}
      </div>
    </div>
  )
}
